import { useAuth } from './useAuth';

// Permissions that are always enabled for all dine-in staff
const BASIC_PERMISSIONS = ['dine_in_view', 'dine_in_create_order'];

// Permissions that default to true but can be explicitly disabled (for waiters)
const OPTIONAL_PERMISSIONS = [
  'dine_in_print_receipt',
  'dine_in_print_invoice',
  'dine_in_process_payment',
];

// Permissions that default to true for all roles
const DEFAULT_TRUE_PERMISSIONS = [
  'dine_in_view_registers',
  'view_orders_tab',
  'view_platform_order_popups',
];

// Special dine-in permissions — only with an explicit `true` (admin roles always; a manager without
// own permissions: yes). Any key not listed above falls here too; the list keeps the rule explicit:
// dine_in_void_payment («Ακύρωση πληρωμής», 6/10/2026) does NOT follow dine_in_process_payment.
export const SPECIAL_DINE_IN_PERMISSIONS = [
  'dine_in_correction_order',
  'dine_in_move_table',
  'dine_in_apply_discount',
  'dine_in_treat',
  'dine_in_cancel_order',
  // «Ακύρωση τραπεζιού με απόδειξη» (migration 281, 25/10/2026): ακυρωτικό 11.4/5.1 — ΔΕΝ ακολουθεί το dine_in_cancel_order.
  'dine_in_cancel_after_receipt',
  'dine_in_void_payment',
  // «Πιάτα ημέρας» από το μενού του ταμείου (migration 278) — ίδιος κανόνας με τον server (services/dailyDishes).
  'daily_dishes_manage',
];

// Roles that always have full access
const ADMIN_ROLES = ['admin', 'store_admin', 'super_admin'];

/**
 * Centralized permission check hook.
 * Returns a `hasPermission(key)` function that respects:
 * - Admin roles → always true
 * - Basic permissions → always true
 * - Optional permissions → default true, can be disabled (`=== false`)
 * - Default-true permissions → default true, can be disabled (`=== false`)
 * - Special permissions → must be explicitly enabled (`=== true`)
 * - Manager fallback → true for all permissions
 */
export const usePermissions = () => {
  const { employee } = useAuth();

  // Special permissions - must be explicitly enabled; manager fallback (no own permissions) - has all
  const hasSpecial = (permission: string): boolean => {
    if (employee?.permissions) {
      return employee.permissions[permission] === true;
    }
    return employee?.role === 'manager';
  };

  const hasPermission = (permission: string): boolean => {
    const role = employee?.role;

    // Admin roles always have all permissions
    if (role && ADMIN_ROLES.includes(role)) return true;

    // Special dine-in permissions — never a default-true path
    if (SPECIAL_DINE_IN_PERMISSIONS.includes(permission)) return hasSpecial(permission);

    // Basic permissions - always on
    if (BASIC_PERMISSIONS.includes(permission)) return true;

    // Optional permissions - default true, can be disabled
    if (OPTIONAL_PERMISSIONS.includes(permission)) {
      if (employee?.permissions && employee.permissions[permission] === false) return false;
      return true;
    }

    // Default-true permissions - default true, can be disabled
    if (DEFAULT_TRUE_PERMISSIONS.includes(permission)) {
      if (employee?.permissions && employee.permissions[permission] === false) return false;
      return true;
    }

    return hasSpecial(permission);
  };

  return { hasPermission };
};
